import { isAxiosError } from 'axios';

/**
 * Prefix for every SPA fingerprint so SPA groups never collide with Function App ProblemIds
 */
const FINGERPRINT_PREFIX = 'spa';

/**
 * Maximum length of the message portion of a fingerprint
 */
const MAX_MESSAGE_LENGTH = 100;

/**
 * Minimal error shape: an Error instance, or the name/message pair Application Insights keeps for
 * automatically collected exceptions
 */
type ErrorLike = Pick<Error, 'name' | 'message'>;

/**
 * Replaces variable parts of a string (GUIDs, Stripe-style IDs, numbers) with placeholders,
 * so otherwise identical errors collapse into one group.
 */
function normalize(value: string): string {
  return value
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '{id}')
    .replace(/\b[a-z]+_[A-Za-z0-9]{8,}\b/g, '{id}')
    .replace(/\d+/g, '{n}');
}

/**
 * Describes an Axios request as `METHOD /route/{id} status`, without query string or IDs
 */
function describeHttpRequest(error: ErrorLike): string | null {
  if (!isAxiosError(error)) {
    return null;
  }

  const method = (error.config?.method ?? 'GET').toUpperCase();
  const route = normalize((error.config?.url ?? '').split('?')[0]);
  const status = error.response?.status ?? 'network';
  return `${method} ${route} ${status}`;
}

/**
 * Returns true when the error is an HTTP 4xx response.
 * These are expected client-side outcomes (validation, not found, conflict) rather than defects,
 * and the API already reports its own failures.
 */
export function isClientHttpError(error: unknown): boolean {
  if (!isAxiosError(error)) {
    return false;
  }

  const status = error.response?.status;
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * Builds a stable fingerprint for an SPA error, used by the error triage feeder to group and
 * deduplicate issues. Unlike the Application Insights ProblemId it never depends on minified
 * stack frames, so it survives rebuilds, and it separates errors that are thrown from shared code
 * (the logger itself, the Axios client).
 *
 * - HTTP errors: `spa|<context>|<METHOD> <route> <status>`
 * - Other errors: `spa|<context>|<ErrorName>: <normalized message>`
 *
 * @param error - The error to fingerprint
 * @param context - Call-site identifier (e.g. "UpdateFeatureTag"); "unhandled" when absent
 */
export function buildErrorFingerprint(error: ErrorLike, context?: string): string {
  const detail =
    describeHttpRequest(error) ??
    `${error.name || 'Error'}: ${normalize(error.message ?? '').slice(0, MAX_MESSAGE_LENGTH)}`;
  return [FINGERPRINT_PREFIX, context || 'unhandled', detail].join('|');
}
